import {
  customElement,
  type TemplateResult,
  property,
  state,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { demoFunc } from './dees-input-dropdown.demo.js';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesInputDropdownPopup } from './dees-input-dropdown-popup.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-dropdown': DeesInputDropdown;
  }
}

@customElement('dees-input-dropdown')
export class DeesInputDropdown extends DeesInputBase<DeesInputDropdown> {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE

  @property()
  accessor options: { option: string; key: string; payload?: any }[] = [];

  @property()
  accessor selectedOption: { option: string; key: string; payload?: any } | null = null;

  // Add value property for form compatibility
  public get value() {
    return this.selectedOption;
  }

  public set value(val: { option: string; key: string; payload?: any } | null) {
    this.selectedOption = val;
  }

  @property({
    type: Boolean,
  })
  accessor enableSearch: boolean = true;

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor vintegrated: boolean = false;

  @state()
  accessor isOpened = false;

  private popupInstance: DeesInputDropdownPopup | null = null;

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      * {
        box-sizing: border-box;
      }

      :host {
        font-family: var(--dees-font-family);
        position: relative;
        color: var(--dees-color-text-secondary);
      }

      .maincontainer {
        display: block;
        position: relative;
      }

      .selectedBox {
        user-select: none;
        position: relative;
        width: 100%;
        height: var(--dees-control-height-lg);
        line-height: calc(var(--dees-control-height-lg) - 2px); /* account for 1px top+bottom border */
        padding: 0 40px 0 var(--dees-spacing-md);
        background: transparent;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-primary);
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .selectedBox:hover:not(.disabled) {
        border-color: var(--dees-color-border-strong);
      }

      .selectedBox:focus-visible {
        outline: none;
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      .selectedBox.disabled {
        background: var(--dees-color-badge-default-bg);
        border-color: var(--dees-color-border-default);
        color: var(--dees-color-text-muted);
        cursor: not-allowed;
        opacity: 0.5;
      }

      /* Dropdown arrow */
      .selectedBox::after {
        content: '';
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid var(--dees-color-text-muted);
        transition: transform var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .selectedBox.open::after {
        transform: translateY(-50%) rotate(180deg);
      }

      /* Visually integrated mode: shed chrome to blend into a host component
         (e.g. a dees-table cell in edit mode). */
      :host([vintegrated]) dees-label {
        display: none;
      }
      :host([vintegrated]) .maincontainer {
        height: var(--dees-control-height-lg);
      }
      :host([vintegrated]) .selectedBox {
        height: var(--dees-control-height-lg);
        line-height: var(--dees-control-height-lg);
        padding: 0 32px 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size-sm);
        border: none;
        border-radius: 0;
        background: transparent;
        box-shadow: none;
        transition: none;
      }
      :host([vintegrated]) .selectedBox:hover:not(.disabled),
      :host([vintegrated]) .selectedBox:focus-visible {
        border: none;
        box-shadow: none;
        background: transparent;
      }
      :host([vintegrated]) .selectedBox::after {
        right: 12px;
        opacity: 0.6;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>
        <div class="maincontainer">
          <div
            class="selectedBox ${this.isOpened ? 'open' : ''} ${this.disabled ? 'disabled' : ''}"
            @click="${() => !this.disabled && this.toggleSelectionBox()}"
            tabindex="${this.disabled ? '-1' : '0'}"
            @keydown="${this.handleSelectedBoxKeydown}"
          >
            ${this.selectedOption?.option || 'Select an option'}
          </div>
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  firstUpdated() {
    this.selectedOption = this.selectedOption || null;
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has('options') && this.popupInstance && this.isOpened) {
      this.popupInstance.updateOptions(this.options);
    }
  }

  public async updateSelection(selectedOption: { option: string; key: string; payload?: any }) {
    this.selectedOption = selectedOption;
    this.closePopup();

    this.dispatchEvent(
      new CustomEvent('selectedOption', {
        detail: selectedOption,
        bubbles: true,
      })
    );

    this.changeSubject.next(this);
  }

  public async toggleSelectionBox() {
    if (this.isOpened) {
      this.closePopup();
      return;
    }

    this.isOpened = true;

    // Get trigger position
    const selectedBox = this.shadowRoot!.querySelector('.selectedBox') as HTMLElement;
    const rect = selectedBox.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const opensToTop = spaceBelow < 300 && spaceAbove > spaceBelow;

    // Create popup if needed
    if (!this.popupInstance) {
      this.popupInstance = new DeesInputDropdownPopup();
    }

    // Configure popup
    this.popupInstance.options = this.options;
    this.popupInstance.enableSearch = this.enableSearch;
    this.popupInstance.opensToTop = opensToTop;
    this.popupInstance.triggerRect = rect;
    this.popupInstance.ownerComponent = this;

    // Listen for popup events
    this.popupInstance.addEventListener('option-selected', this.handleOptionSelected);
    this.popupInstance.addEventListener('close-request', this.handleCloseRequest);
    this.popupInstance.addEventListener('reposition-request', this.handleRepositionRequest);

    // Show popup (creates window layer, appends to document.body)
    await this.popupInstance.show();

    // Focus search input
    if (this.enableSearch) {
      await this.popupInstance.focusSearchInput();
    }
  }

  private closePopup(): void {
    this.isOpened = false;

    if (this.popupInstance) {
      this.popupInstance.removeEventListener('option-selected', this.handleOptionSelected);
      this.popupInstance.removeEventListener('close-request', this.handleCloseRequest);
      this.popupInstance.removeEventListener('reposition-request', this.handleRepositionRequest);
      this.popupInstance.hide();
    }
  }

  private handleOptionSelected = (event: Event): void => {
    const detail = (event as CustomEvent).detail;
    this.updateSelection(detail);
  };

  private handleCloseRequest = (): void => {
    this.closePopup();
  };

  private handleRepositionRequest = (): void => {
    if (!this.popupInstance || !this.isOpened) return;

    const selectedBox = this.shadowRoot!.querySelector('.selectedBox') as HTMLElement;
    if (!selectedBox) return;

    const rect = selectedBox.getBoundingClientRect();

    // Close if trigger scrolled off-screen
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      this.closePopup();
      return;
    }

    // Update position
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    this.popupInstance.opensToTop = spaceBelow < 300 && spaceAbove > spaceBelow;
    this.popupInstance.triggerRect = rect;
  };

  private handleSelectedBoxKeydown(event: KeyboardEvent) {
    if (this.disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleSelectionBox();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpened) {
        this.toggleSelectionBox();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.isOpened) {
        this.closePopup();
      }
    }
  }

  public getValue(): { option: string; key: string; payload?: any } | null {
    return this.selectedOption;
  }

  public setValue(value: { option: string; key: string; payload?: any }): void {
    this.selectedOption = value;
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    if (this.popupInstance) {
      this.popupInstance.removeEventListener('option-selected', this.handleOptionSelected);
      this.popupInstance.removeEventListener('close-request', this.handleCloseRequest);
      this.popupInstance.removeEventListener('reposition-request', this.handleRepositionRequest);
      this.popupInstance.hide();
      this.popupInstance = null;
    }
  }
}
